<template>
    <section class="project-list-section">
        <div class="project-list-header">
            <div class="project-title-wrap">
                <h2>工程案例</h2>
                <span class="title-watermark">ENGINEERING CASES</span>
            </div>
            <!-- <router-link to="/project" class="more-link">查看更多</router-link> -->
        </div>
        <div class="project-carousel" v-if="projects.length">
            <div class="carousel-wrapper" @touchstart="handleTouchStart" @touchmove="handleTouchMove"
                @touchend="handleTouchEnd">
                <div class="carousel-group" v-for="(group, idx) in groupedProjects" :key="idx" v-show="idx === current"
                    :style="dragStyle">
                    <div class="project-card-wrap" v-for="item in group" :key="item.id">
                        <ProjectCard :image="item.coverImage" :title="item.projectName" :id="item.id" />
                    </div>
                </div>
            </div>
            <button class="carousel-btn prev" @click="prev">‹</button>
            <button class="carousel-btn next" @click="next">›</button>
            <div class="carousel-indicators" v-if="groupedProjects.length > 1">
                <span v-for="(group, idx) in groupedProjects" :key="idx" :class="{ active: idx === current }"
                    @click="goTo(idx)"></span>
            </div>
        </div>
        <div v-else class="project-placeholder">暂无项目</div>
    </section>
</template>

<script>
import { getHomeProjectList } from '../api/project';
import ProjectCard from './ProjectCard.vue';

export default {
    name: 'ProjectList',
    components: { ProjectCard },
    data() {
        return {
            projects: [],
            current: 0,
            groupSize: 4, // 默认PC端每组4个
            touchStartX: 0,
            touchStartY: 0,
            touchEndX: 0,
            touchEndY: 0,
            isSwiping: false,
            dragOffset: 0, // 拖拽偏移
            isDragging: false
        };
    },
    computed: {
        groupedProjects() {
            const groups = [];
            for (let i = 0; i < this.projects.length; i += this.groupSize) {
                groups.push(this.projects.slice(i, i + this.groupSize));
            }
            return groups;
        },
        dragStyle() {
            if (this.isDragging && Math.abs(this.dragOffset) > 0) {
                return {
                    transform: `translateX(${this.dragOffset}px)`,
                    transition: 'none'
                };
            } else {
                return {
                    transform: 'translateX(0)',
                    transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)'
                };
            }
        }
    },
    mounted() {
        this.fetchProjects();
        this.updateGroupSize();
        window.addEventListener('resize', this.updateGroupSize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateGroupSize);
    },
    methods: {
        fetchProjects() {
            getHomeProjectList().then(res => {
                if (res.data.code === 200 && Array.isArray(res.data.data)) {
                    this.projects = res.data.data;
                }
            });
        },
        updateGroupSize() {
            const width = window.innerWidth;
            if (width <= 600) {
                this.groupSize = 1;
            } else if (width <= 900) {
                this.groupSize = 2;
            } else {
                this.groupSize = 4;
            }
            // 当前页码重置，避免切换分组后越界
            this.current = 0;
        },
        next() {
            this.current = (this.current + 1) % this.groupedProjects.length;
        },
        prev() {
            this.current = (this.current - 1 + this.groupedProjects.length) % this.groupedProjects.length;
        },
        goTo(idx) {
            this.current = idx;
        },
        // 触摸事件处理
        handleTouchStart(event) {
            this.touchStartX = event.touches[0].clientX;
            this.touchStartY = event.touches[0].clientY;
            this.isSwiping = false;
            this.isDragging = true;
            this.dragOffset = 0;
            // 预留：如有自动切换，手动滑动时可暂停
        },
        handleTouchMove(event) {
            if (!this.touchStartX || !this.touchStartY) return;
            this.touchEndX = event.touches[0].clientX;
            this.touchEndY = event.touches[0].clientY;
            const deltaX = this.touchEndX - this.touchStartX;
            const deltaY = this.touchEndY - this.touchStartY;
            // 判断是否为水平滑动
            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                this.isSwiping = true;
                this.dragOffset = deltaX;
                event.preventDefault();
            }
        },
        handleTouchEnd(event) {
            if (!this.isSwiping || !this.touchStartX || !this.touchEndX) {
                this.resetTouchState();
                // 预留：如有自动切换，滑动结束后可恢复
                return;
            }
            const deltaX = this.touchEndX - this.touchStartX;
            const minSwipeDistance = 30; // 降低阈值
            if (Math.abs(deltaX) > minSwipeDistance) {
                if (deltaX < 0) {
                    // 向左滑动，显示下一组
                    this.next();
                } else {
                    // 向右滑动，显示上一组
                    this.prev();
                }
            }
            this.resetTouchState();
            // 预留：如有自动切换，滑动结束后可恢复
        },
        resetTouchState() {
            this.touchStartX = 0;
            this.touchStartY = 0;
            this.touchEndX = 0;
            this.touchEndY = 0;
            this.isSwiping = false;
            this.isDragging = false;
            this.dragOffset = 0;
        }
    }
};
</script>

<style scoped>
.project-list-section {
    background: #fff;
    padding: 48px 0 56px 0;
    margin-bottom: 40px;
    border-radius: 20px;
    box-shadow: 0 4px 24px rgba(30, 60, 120, 0.06);
    width: 97%;
    max-width: none;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    overflow: hidden;
}

.project-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto 24px auto;
}

.project-title-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 18px;
    width: 100%;
    min-width: unset;
    padding-right: 0;
}

.title-watermark {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: -10px;
    font-size: 2.2rem;
    font-weight: 900;
    color: #1a2a47;
    opacity: 0.09;
    letter-spacing: 0.18em;
    user-select: none;
    pointer-events: none;
    z-index: 1;
    white-space: nowrap;
}

.project-title-wrap h2 {
    position: relative;
    z-index: 2;
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1a2a47;
    letter-spacing: 1px;
    text-align: center;
}

.more-link {
    font-size: 1rem;
    color: #2563eb;
    font-weight: 500;
    text-decoration: underline;
}

.project-carousel {
    position: relative;
    width: 100%;
    margin: 0 auto;
    padding: 24px 0 32px 0;
}

.carousel-wrapper {
    width: 100%;
    overflow: hidden;
}

.carousel-group {
    display: flex;
    gap: 32px;
    justify-content: center;
    align-items: stretch;
    min-height: 340px;
    transition: opacity 0.5s;
}

.project-card-wrap {
    flex: 1 1 0;
    max-width: 340px;
    min-width: 0;
    display: flex;
    justify-content: center;
    height: 400px;
}

.carousel-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: #fff;
    color: #2563eb;
    border: none;
    font-size: 32px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    cursor: pointer;
    z-index: 2;
    box-shadow: 0 2px 8px rgba(30, 60, 120, 0.10);
}

.carousel-btn.prev {
    left: 10px;
}

.carousel-btn.next {
    right: 10px;
}

.carousel-indicators {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 18px;
}

.carousel-indicators span {
    display: block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #2563eb;
    opacity: 0.3;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.2s;
}

.carousel-indicators span.active {
    opacity: 1;
    transform: scale(1.2);
}

@media (max-width: 1200px) {
    .project-card-wrap {
        max-width: 280px;
        height: 340px;
    }
}

@media (max-width: 900px) {
    .carousel-group {
        gap: 16px;
    }

    .project-card-wrap {
        max-width: 33.33%;
        height: 320px;
    }
}

@media (max-width: 600px) {
    .carousel-group {
        gap: 8px;
    }

    .project-card-wrap {
        max-width: 100%;
        height: 260px;
    }

    .carousel-btn {
        width: 32px;
        height: 32px;
        font-size: 22px;
    }
}

@media (max-width: 768px) {
    .project-title-wrap h2 {
        font-size: 1.18rem;
    }

    .title-watermark {
        font-size: 1.2rem;
    }
}
</style>